import {observer} from "mobx-react";
import "./TableauBlanc.scss";
import {useStores} from "../../../stores/Context";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {fabric} from "fabric";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import SidebarWidgets from "../../SidebarWidgets/SidebarWidgets";
import ContextMenu from "./ContextMenu";
import CanvaSidebar from "./CanvaSidebar";
import MiniMap from "./MiniMap";
import {useFabricEvents} from "./useFabricEvents";
import useCanvasManipulation from "./useCanvasManipulation";
import WhiteboardActionModal from "./WhiteboardActionModal";
import {getFloatingToolMenuPosition} from "./toolMenuPosition";
import {LOCAL_WHITEBOARD_REFRESH_GUARD_MS, shouldSkipWhiteboardRefresh} from "./whiteboardRefresh";
import {getSafeWhiteboardBounds} from "./whiteboardThumbnail";
import {clearFabricCanvasHost, mountFabricCanvas} from "./whiteboardCanvasHost";
import {
    CONNECTOR_JSON_PROPERTIES,
    createWhiteboardLink,
    sanitiseWhiteboardCanvasPayload,
} from "./whiteboardLinks";
import {getConnectorSelectionStep} from "./connectorSelectionFlow";
import "./WhiteboardV2.scss";

const STICKY_COLORS = [
    {label: "Jaune", value: "#FFE68A"},
    {label: "Rose", value: "#FFB7C9"},
    {label: "Bleu", value: "#BCE9FF"},
    {label: "Vert", value: "#C9F2C8"},
    {label: "Violet", value: "#DEC8FF"},
];

const STICKY_FORMATS = [
    {id: "square", label: "Carré", icon: "□", width: 252, minHeight: 252, padding: 24},
    {id: "classic", label: "Classique", icon: "▣", width: 280, minHeight: 220, padding: 24},
    {id: "wide", label: "Panoramique", icon: "▭", width: 380, minHeight: 200, padding: 26},
    {id: "tall", label: "Vertical", icon: "▯", width: 220, minHeight: 320, padding: 22},
];

const CONNECTOR_STYLES = [
    {id: "solid", label: "Lien", icon: "—"},
    {id: "dashed", label: "Lien pointillé", icon: "┄"},
    {id: "arrow", label: "Flèche", icon: "→"},
];

const nextObjectId = (kind) => `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const Canva = observer(() => {
    const {TableauBlancStore, bibliothequeImagesStore, widgetStore, PlanningActionsStore, uiStore} = useStores();
    const canvasRef = useRef(null);
    const canvasMountRef = useRef(null);
    const whiteboardRootRef = useRef(null);
    const isLoadingCanvasRef = useRef(true);
    const isTextEditingRef = useRef(false);
    const pendingSaveRef = useRef(null);
    const saveCanvasRef = useRef(null);
    const connectorStartRef = useRef(null);
    const ignoreRemoteCanvasRefreshUntilRef = useRef(0);
    const connectorModeRef = useRef(null);
    const [tbNom, setTbNom] = useState(null);
    const [canvas, setCanvas] = useState(null);
    const [lastSaveTime, setLastSaveTime] = useState(null); // État pour la date/heure de la dernière sauvegarde
    const [actionModalOpen, setActionModalOpen] = useState(false);
    const [isCreatingAction, setIsCreatingAction] = useState(false);
    const [createdPlanningTask, setCreatedPlanningTask] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const {
        copyObject,
        pasteObject,
        bringToFront,
        sendToBack,
        moveForward,
        moveBackward
    } = useCanvasManipulation(canvas);

    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [toolMenu, setToolMenu] = useState(null);
    const [toolMenuPosition, setToolMenuPosition] = useState({top: 0, left: 0, width: 0});
    const [stickyFormat, setStickyFormat] = useState("classic");
    const [connectorHint, setConnectorHint] = useState(null);

    const [drawingSettings, setDrawingSettings] = useState({
        brushSize: 1, // Épaisseur du stylo
        brushType: "PencilBrush",
        color: "#0B64CB",
        isErasing: false,
    });

    const [textSettings, setTextSettings] = useState({
        fontSize: 20,
        fontFamily: "Arial",
        fontWeight: "normal",
        fontStyle: "normal",
        color: "#0B64CB", // couleur du texte
        backgroundColor: '', // couleur de fond du texte
    });


    const [shapeSettings, setShapeSettings] = useState({
        type: "rect", // type de forme par défaut
        fill: "#0B64CB", // couleur de la forme
        backgroundColor: "",
    });

    const [imageSettings, setImageSettings] = useState({
        stroke: null,
        strokeWidth: 0,
    });

    const [canvasSettings, setCanvasSettings] = useState({
        backgroundColor: "#FFFFFF",
    });

    const [drawingMode, setDrawingMode] = useState(false); // État du mode dessin
    const [isErasing, setIsErasing] = useState(false);
    const [panMode, setPanMode] = useState(false);


    const fileInputRef = useRef(null);

    const [selectedObject, setSelectedObject] = useState(null);
    const historyRef = useRef({entries: [], index: -1, isRestoring: false});
    const [historyState, setHistoryState] = useState({canUndo: false, canRedo: false});

    const [menuPosition, setMenuPosition] = useState({x: 0, y: 0});

    const [viewportInfo, setViewportInfo] = useState({zoom: 1, x: 0, y: 0});

    const applySelectionMode = useCallback(() => {
        if (canvas) {
            canvas.isDrawingMode = false;
            canvas.selection = true;
            canvas.skipTargetFind = false;
            canvas.defaultCursor = "default";
            canvas.hoverCursor = "move";
            canvas.requestRenderAll();
        }
        connectorStartRef.current = null;
        setDrawingMode(false);
        setIsErasing(false);
        setPanMode(false);
        connectorModeRef.current = null;
        setConnectorHint(null);
    }, [canvas]);

    const scheduleCanvasSave = useCallback((delay = 350) => {
        window.clearTimeout(pendingSaveRef.current);
        pendingSaveRef.current = window.setTimeout(() => saveCanvasRef.current?.(), delay);
    }, []);

    useEffect(() => () => window.clearTimeout(pendingSaveRef.current), []);

    useEffect(() => {
        const syncFullscreenState = () => setIsFullscreen(document.fullscreenElement === whiteboardRootRef.current);
        document.addEventListener("fullscreenchange", syncFullscreenState);
        return () => document.removeEventListener("fullscreenchange", syncFullscreenState);
    }, []);

    const closeToolMenu = useCallback(() => {
        setToolMenu(null);
        applySelectionMode();
    }, [applySelectionMode]);

    const toggleToolMenu = useCallback((menuName, event) => {
        event.preventDefault();
        event.stopPropagation();

        if (toolMenu === menuName) {
            closeToolMenu();
            return;
        }

        setToolMenuPosition(getFloatingToolMenuPosition({
            rect: event.currentTarget.getBoundingClientRect(),
            menuName,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        }));
        setToolMenu(menuName);
    }, [closeToolMenu, toolMenu]);

    useEffect(() => {
        if (!toolMenu) return undefined;

        const dismissToolMenu = (event) => {
            if (event.target.closest(".wbFloatingToolMenu") || event.target.closest(`[data-wb-tool-trigger="${toolMenu}"]`)) return;
            closeToolMenu();
        };
        const dismissOnEscape = (event) => {
            if (event.key === "Escape") closeToolMenu();
        };

        window.addEventListener("pointerdown", dismissToolMenu);
        window.addEventListener("keydown", dismissOnEscape);
        return () => {
            window.removeEventListener("pointerdown", dismissToolMenu);
            window.removeEventListener("keydown", dismissOnEscape);
        };
    }, [closeToolMenu, toolMenu]);

    const makeObjectInteractive = useCallback((object) => {
        if (!object) return object;

        const isStickyNote = object.customType === "sticky_note";
        const isConnector = ["connector", "connector_head"].includes(object.customType);

        if (!object.objectId && object.type) {
            object.set({objectId: nextObjectId(isConnector ? "connector" : object.customType || object.type)});
        }

        object.set({
            selectable: object.selectable !== false,
            evented: object.evented !== false,
            hasControls: true,
            hasBorders: true,
            transparentCorners: false,
            cornerStyle: "circle",
            cornerColor: "#00AEEF",
            cornerStrokeColor: "#FFFFFF",
            borderColor: "#00AEEF",
            padding: 7,
        });

        if (isStickyNote) {
            // Un post-it est un Textbox : ses poignées latérales modifient sa
            // largeur et font revenir le texte à la ligne. Les coins Fabric
            // feraient une mise à l’échelle bitmap du texte, ce qui l’écrase.
            const effectiveWidth = Math.max(object.minWidth || 180, object.width * (object.scaleX || 1));
            const stickyMinHeight = object.stickyMinHeight || 220;
            const stickyPadding = object.stickyPadding || 22;
            object.set({
                width: effectiveWidth,
                scaleX: 1,
                scaleY: 1,
                lockScalingFlip: true,
                lockScalingY: true,
                stickyMinHeight,
                stickyPadding,
                padding: stickyPadding,
            });
            object.initDimensions?.();
            object.set({height: Math.max(object.height, stickyMinHeight)});
            object.setCoords?.();
            // Ne conserver que les deux contrôles Textbox de largeur et la
            // rotation : aucun contrôle vertical ou de coin ne peut écraser le texte.
            object.controls = {
                ml: object.controls.ml,
                mr: object.controls.mr,
                mtr: object.controls.mtr,
            };
        }

        if (isConnector) {
            // Les liaisons suivent leurs deux objets : elles sont sélectionnables
            // pour être supprimées, mais ne se déplacent ni ne se redimensionnent seules.
            object.set({
                hasControls: false,
                lockMovementX: true,
                lockMovementY: true,
                lockScalingX: true,
                lockScalingY: true,
                lockRotation: true,
                selectable: object.customType !== "connector_head",
                evented: object.customType !== "connector_head",
            });
            object.controls = {};
        }

        return object;
    }, []);

    const getInsertPosition = useCallback((offset = 0) => {
        if (!canvas) return {left: 140 + offset, top: 140 + offset};

        const zoom = canvas.getZoom() || 1;
        const viewport = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];

        return {
            left: (-viewport[4] / zoom) + (canvas.getWidth() / (2 * zoom)) - 110 + offset,
            top: (-viewport[5] / zoom) + (canvas.getHeight() / (2 * zoom)) - 80 + offset,
        };
    }, [canvas]);

    const updateConnectorsForObject = useCallback((target) => {
        if (!canvas || !target?.objectId) return;

        canvas.getObjects()
            .filter(object => object.customType === "connector" && (object.connectorFromId === target.objectId || object.connectorToId === target.objectId))
            .forEach(connector => {
                const source = canvas.getObjects().find(object => object.objectId === connector.connectorFromId);
                const destination = canvas.getObjects().find(object => object.objectId === connector.connectorToId);

                if (!source || !destination) {
                    canvas.getObjects()
                        .filter(object => object.customType === "connector_head" && object.connectorParentId === connector.objectId)
                        .forEach(head => canvas.remove(head));
                    canvas.remove(connector);
                    return;
                }

                source.setCoords?.();
                destination.setCoords?.();
                const from = source.getCenterPoint();
                const to = destination.getCenterPoint();
                connector.set({x1: from.x, y1: from.y, x2: to.x, y2: to.y});
                connector.setCoords?.();

                if (connector.connectorStyle === "arrow") {
                    const head = canvas.getObjects().find(object => object.customType === "connector_head" && object.connectorParentId === connector.objectId);
                    if (head) {
                        const angle = (Math.atan2(to.y - from.y, to.x - from.x) * 180 / Math.PI) + 90;
                        head.set({left: to.x, top: to.y, angle});
                        head.setCoords?.();
                    }
                }
            });

        canvas.requestRenderAll();
    }, [canvas]);

    const removeConnectorsForObject = useCallback((target) => {
        if (!canvas || !target?.objectId) return;

        if (target.customType === "connector") {
            canvas.getObjects()
                .filter(object => object.customType === "connector_head" && object.connectorParentId === target.objectId)
                .forEach(head => canvas.remove(head));
            return;
        }

        if (target.customType === "connector_head") return;

        canvas.getObjects()
            .filter(object => ["connector", "connector_head"].includes(object.customType) && (object.connectorFromId === target.objectId || object.connectorToId === target.objectId))
            .forEach(connector => canvas.remove(connector));
    }, [canvas]);

    const serialiseCanvas = useCallback((fabricCanvas) => JSON.stringify(fabricCanvas.toJSON(CONNECTOR_JSON_PROPERTIES)), []);

    const updateHistoryState = useCallback(() => {
        const history = historyRef.current;
        setHistoryState({
            canUndo: history.index > 0,
            canRedo: history.index >= 0 && history.index < history.entries.length - 1,
        });
    }, []);

    const resetCanvasHistory = useCallback((fabricCanvas) => {
        if (!fabricCanvas) return;

        historyRef.current = {
            entries: [serialiseCanvas(fabricCanvas)],
            index: 0,
            isRestoring: false,
        };
        updateHistoryState();
    }, [serialiseCanvas, updateHistoryState]);

    const recordCanvasHistory = useCallback(() => {
        if (!canvas || isLoadingCanvasRef.current || historyRef.current.isRestoring) return;

        const snapshot = serialiseCanvas(canvas);
        const history = historyRef.current;

        if (history.entries[history.index] === snapshot) return;

        history.entries = history.entries.slice(0, history.index + 1);
        history.entries.push(snapshot);
        history.index = history.entries.length - 1;

        // Conserver un historique suffisant pour le tactile sans laisser un
        // canevas riche occuper une quantité de mémoire non bornée.
        if (history.entries.length > 50) {
            history.entries.shift();
            history.index -= 1;
        }

        updateHistoryState();
    }, [canvas, serialiseCanvas, updateHistoryState]);

    const loadCanvasData = useCallback(async (fabricCanvas) => {
        try {
            const response = await TableauBlancStore.loadCanvas();

            if (!response.canvasData) {
                console.error("Aucune donnée de canevas trouvée");
                isLoadingCanvasRef.current = false;
                return;
            }

            // Les événements Fabric déclenchés par loadFromJSON ne doivent pas
            // relancer une sauvegarde locale : la donnée provient déjà du serveur.
            isLoadingCanvasRef.current = true;
            const safeCanvasData = sanitiseWhiteboardCanvasPayload(response.canvasData);
            await new Promise((resolve) => {
                try {
                    fabricCanvas.loadFromJSON(safeCanvasData, () => {
                        try {
                            fabricCanvas.getObjects().forEach(makeObjectInteractive);
                            fabricCanvas.renderAll();
                            setCanvasSettings(previous => ({...previous, backgroundColor: fabricCanvas.backgroundColor || "#FFFFFF"}));
                            setTbNom(response.canva_nom);
                            resetCanvasHistory(fabricCanvas);
                        } catch (error) {
                            // Un ancien lien incomplet ne doit jamais faire écran blanc :
                            // l'utilisateur conserve les objets qui restent chargeables.
                            console.error("Erreur de restauration d'une liaison Tableau Blanc", error);
                        } finally {
                            isLoadingCanvasRef.current = false;
                            resolve();
                        }
                    });
                } catch (error) {
                    console.error("Erreur de chargement du canevas Tableau Blanc", error);
                    fabricCanvas.clear();
                    fabricCanvas.setBackgroundColor("#FFFFFF", fabricCanvas.renderAll.bind(fabricCanvas));
                    isLoadingCanvasRef.current = false;
                    resolve();
                }
            });
        } catch (error) {
            isLoadingCanvasRef.current = false;
            console.error("Erreur lors du chargement du canevas", error);
        }
    }, [TableauBlancStore, makeObjectInteractive, resetCanvasHistory]);

    const saveCanvas = async () => {
        if (!canvas || isTextEditingRef.current) return;

        // Le broker peut réémettre une sauvegarde locale sans client_id.
        // Recharger Fabric à ce moment casse la sélection du second objet lié.
        ignoreRemoteCanvasRefreshUntilRef.current = Date.now() + LOCAL_WHITEBOARD_REFRESH_GUARD_MS;
        try {
            canvas.renderAll();
            const jsonCanva = sanitiseWhiteboardCanvasPayload(canvas.toJSON(CONNECTOR_JSON_PROPERTIES));
            const imageBase64 = await createCanvasThumbnail(canvas);
            await TableauBlancStore.saveCanvas(jsonCanva);
            await TableauBlancStore.saveCanvasAsImage(imageBase64);
            ignoreRemoteCanvasRefreshUntilRef.current = Date.now() + LOCAL_WHITEBOARD_REFRESH_GUARD_MS;
            setLastSaveTime(new Date());
        } catch (e) {
            console.error("Erreur lors de la sauvegarde du canevas :", e);
        }
    };

    saveCanvasRef.current = saveCanvas;

    const getCroppedCanvasImage = (canvas) => {
        if (!canvas) return null;

        const originalVpt = canvas.viewportTransform ? [...canvas.viewportTransform] : null;
        const originalZoom = canvas.getZoom ? canvas.getZoom() : 1;
        const restoreViewport = () => {
            if (originalVpt && canvas.setViewportTransform) {
                canvas.setViewportTransform(originalVpt);
            }
            if (canvas.setZoom) {
                canvas.setZoom(originalZoom);
            }
            canvas.renderAll();
        };

        try {
            // La miniature est non critique : une géométrie Fabric incomplète
            // ne doit jamais interrompre la sauvegarde du tableau ou le rendu.
            canvas.setViewportTransform?.([1, 0, 0, 1, 0, 0]);
            canvas.setZoom?.(1);
            canvas.renderAll();

            const bounds = getSafeWhiteboardBounds(canvas.getObjects());
            if (!bounds) {
                return canvas.toDataURL({format: "png", quality: 1});
            }

            return canvas.toDataURL({
                format: "png",
                quality: 1,
                ...bounds,
            });
        } catch (error) {
            console.warn("Miniature Tableau Blanc ignorée : géométrie non exportable", error);
            try {
                return canvas.toDataURL({format: "png", quality: 1});
            } catch (fallbackError) {
                return null;
            }
        } finally {
            restoreViewport();
        }
    };

    const createCanvasThumbnail = (fabricCanvas) => new Promise((resolve) => {
        const thumbnail = document.createElement("canvas");
        thumbnail.width = 960;
        thumbnail.height = 540;
        const context = thumbnail.getContext("2d");
        const visibleObjects = fabricCanvas.getObjects().filter((object) => object.visible && object.customType !== "connector_head");

        const background = context.createLinearGradient(0, 0, thumbnail.width, thumbnail.height);
        background.addColorStop(0, "#F4FBFD");
        background.addColorStop(1, "#E6F3F7");
        context.fillStyle = background;
        context.fillRect(0, 0, thumbnail.width, thumbnail.height);

        context.fillStyle = "rgba(0, 126, 186, .12)";
        for (let x = 42; x < thumbnail.width; x += 34) {
            for (let y = 112; y < thumbnail.height; y += 34) {
                context.beginPath();
                context.arc(x, y, 1.4, 0, Math.PI * 2);
                context.fill();
            }
        }

        context.fillStyle = "#00AEEF";
        context.fillRect(0, 0, thumbnail.width, 84);
        context.fillStyle = "#FFFFFF";
        context.font = "700 30px Arial, sans-serif";
        context.fillText("TABLEAU BLANC", 38, 51);
        context.fillStyle = "rgba(255,255,255,.82)";
        context.font = "600 20px Arial, sans-serif";
        context.fillText(tbNom || "Espace de travail", 310, 51);

        if (!visibleObjects.length) {
            context.fillStyle = "rgba(255,255,255,.92)";
            context.fillRect(196, 190, 568, 174);
            context.strokeStyle = "rgba(0,126,186,.2)";
            context.lineWidth = 3;
            context.strokeRect(196, 190, 568, 174);
            context.fillStyle = "#00AEEF";
            context.font = "700 34px Arial, sans-serif";
            context.fillText("Votre espace de travail", 330, 258);
            context.fillStyle = "#55727C";
            context.font = "400 23px Arial, sans-serif";
            context.fillText("Ajoutez un post-it, une image ou une idée.", 284, 304);
            resolve(thumbnail.toDataURL("image/png"));
            return;
        }

        const source = new Image();
        source.onload = () => {
            const maxWidth = thumbnail.width - 72;
            const maxHeight = thumbnail.height - 132;
            const scale = Math.min(maxWidth / source.width, maxHeight / source.height);
            const width = source.width * scale;
            const height = source.height * scale;
            const left = (thumbnail.width - width) / 2;
            const top = 104 + (maxHeight - height) / 2;

            context.fillStyle = "rgba(0,35,53,.12)";
            context.fillRect(left + 8, top + 10, width, height);
            context.fillStyle = "#FFFFFF";
            context.fillRect(left, top, width, height);
            context.drawImage(source, left, top, width, height);
            resolve(thumbnail.toDataURL("image/png"));
        };
        source.onerror = () => resolve(thumbnail.toDataURL("image/png"));
        const croppedCanvasImage = getCroppedCanvasImage(fabricCanvas);
        if (!croppedCanvasImage) {
            resolve(thumbnail.toDataURL("image/png"));
            return;
        }
        source.src = croppedCanvasImage;
    });

    const restoreHistory = useCallback((direction) => {
        if (!canvas) return;

        const history = historyRef.current;
        const nextIndex = history.index + direction;
        if (nextIndex < 0 || nextIndex >= history.entries.length) return;

        history.isRestoring = true;
        isLoadingCanvasRef.current = true;
        canvas.discardActiveObject();

        try {
            canvas.loadFromJSON(sanitiseWhiteboardCanvasPayload(history.entries[nextIndex]), () => {
                history.index = nextIndex;
                history.isRestoring = false;
                isLoadingCanvasRef.current = false;
                canvas.getObjects().forEach(makeObjectInteractive);
                canvas.renderAll();
                setSelectedObject(null);
                updateHistoryState();
                saveCanvasRef.current?.();
            });
        } catch (error) {
            console.error("Impossible de restaurer l'historique du Tableau Blanc", error);
            history.isRestoring = false;
            isLoadingCanvasRef.current = false;
        }
    }, [canvas, makeObjectInteractive, updateHistoryState]);

    const undoCanvas = useCallback(() => restoreHistory(-1), [restoreHistory]);
    const redoCanvas = useCallback(() => restoreHistory(1), [restoreHistory]);

    const handleConnectorSelection = useCallback((target) => {
        const step = getConnectorSelectionStep({
            style: connectorModeRef.current,
            source: connectorStartRef.current,
            target,
        });

        if (step.type === "ignore") return false;

        if (step.type === "source") {
            connectorStartRef.current = step.source;
            setConnectorHint("Sélectionnez maintenant l’objet à relier");
            return true;
        }

        if (step.type === "same-object") {
            setConnectorHint("Choisissez un autre objet pour terminer le lien");
            return true;
        }

        try {
            const {connector, head} = createWhiteboardLink({
                fabric,
                source: step.source,
                target: step.target,
                style: step.style,
                createId: nextObjectId,
            });

            makeObjectInteractive(connector);
            if (head) makeObjectInteractive(head);
            canvas.add(connector);
            if (head) canvas.add(head);
            canvas.sendToBack(connector);
            if (head) canvas.sendToBack(head);
        } catch (error) {
            console.error("Impossible de créer la liaison Tableau Blanc", error);
        } finally {
            connectorStartRef.current = null;
            connectorModeRef.current = null;
            setConnectorHint(null);
        }

        canvas.discardActiveObject();
        canvas.requestRenderAll();
        scheduleCanvasSave(80);
        return true;
    }, [canvas, makeObjectInteractive, scheduleCanvasSave]);

    useFabricEvents({
        canvas,
        setSelectedObject,
        setSidebarVisible,
        setMenuPosition,
        setDrawingMode,
        panMode,
        setViewportInfo
        , setTextSettings
        , setShapeSettings
        , setImageSettings
        , isLoadingCanvasRef
        , isErasing
        , scheduleCanvasSave
        , isTextEditingRef
        , onObjectMoving: updateConnectorsForObject
        , onObjectModified: updateConnectorsForObject
        , onObjectRemoved: removeConnectorsForObject
        , onConnectorSelection: handleConnectorSelection
        , onTouchGestureStart: applySelectionMode
    });

    useEffect(() => {
        if (!canvas) return;

        const captureHistory = () => recordCanvasHistory();

        canvas.on("object:added", captureHistory);
        canvas.on("object:modified", captureHistory);
        canvas.on("object:removed", captureHistory);
        canvas.on("path:created", captureHistory);

        return () => {
            canvas.off("object:added", captureHistory);
            canvas.off("object:modified", captureHistory);
            canvas.off("object:removed", captureHistory);
            canvas.off("path:created", captureHistory);
        };
    }, [canvas, recordCanvasHistory]);

    useEffect(() => {
        const handleHistoryShortcut = (event) => {
            const activeElement = document.activeElement;
            const isTyping = activeElement && ["INPUT", "TEXTAREA", "SELECT"].includes(activeElement.tagName);
            const isEditingTextbox = canvas?.getActiveObject()?.type === "textbox" && canvas.getActiveObject().isEditing;

            if (isTyping || isEditingTextbox) return;

            const isModifierPressed = event.ctrlKey || event.metaKey;
            const key = event.key.toLowerCase();

            if (event.key === "Delete" || event.key === "Backspace") {
                const activeObjects = canvas?.getActiveObjects() || [];
                if (activeObjects.length) {
                    event.preventDefault();
                    canvas.discardActiveObject();
                    activeObjects.forEach(object => canvas.remove(object));
                    canvas.requestRenderAll();
                    setSelectedObject(null);
                }
                return;
            }

            if (isModifierPressed && key === "c") {
                event.preventDefault();
                copyObject();
                return;
            }

            if (isModifierPressed && key === "v") {
                event.preventDefault();
                pasteObject();
                return;
            }

            if (!isModifierPressed || key !== "z") return;

            event.preventDefault();
            if (event.shiftKey) {
                redoCanvas();
            } else {
                undoCanvas();
            }
        };

        window.addEventListener("keydown", handleHistoryShortcut);
        return () => window.removeEventListener("keydown", handleHistoryShortcut);
    }, [canvas, copyObject, pasteObject, redoCanvas, undoCanvas]);

    const addShape = (type = shapeSettings.type || "rect") => {
        if (!canvas) return;

        const {left, top} = getInsertPosition();
        const options = {left, top, fill: shapeSettings.fill, objectId: nextObjectId("shape")};
        let shape;

        switch (type) {
            case "circle":
                shape = new fabric.Circle({...options, radius: 72});
                break;
            case "triangle":
                shape = new fabric.Triangle({...options, width: 152, height: 132});
                break;
            case "line":
                shape = new fabric.Line([left, top, left + 170, top], {stroke: shapeSettings.fill, strokeWidth: 5, objectId: nextObjectId("line")});
                break;
            case "arrow":
                shape = new fabric.Path("M 12 0 L 0 7 L 12 14 L 12 9 L 36 9 L 36 5 L 12 5 z", {
                    ...options,
                    scaleX: 4.5,
                    scaleY: 4.5,
                    customType: "arrow",
                });
                break;
            case "rect":
            default:
                shape = new fabric.Rect({...options, width: 176, height: 116, rx: 10, ry: 10});
                break;
        }

        makeObjectInteractive(shape);
        canvas.add(shape);
        canvas.setActiveObject(shape);
        canvas.requestRenderAll();
        closeToolMenu();
    };

    const changeSelectedShapeType = (newType) => {
        const activeObject = canvas.getActiveObject();
        if (!activeObject) return;

        const {left, top, fill, stroke} = activeObject;

        // Déterminer la bonne couleur à appliquer
        const color = stroke ?? fill;

        // Supprimer l'ancien objet
        canvas.remove(activeObject);

        // Créer une nouvelle forme avec les mêmes propriétés de base
        let newShape;

        switch (newType) {
            case "rect":
                newShape = new fabric.Rect({
                    left, top, fill: color, width: 100, height: 100
                });
                break;
            case "circle":
                newShape = new fabric.Circle({
                    left, top, fill: color, radius: 50
                });
                break;
            case "triangle":
                newShape = new fabric.Triangle({
                    left, top, fill: color, width: 100, height: 100
                });
                break;
            case "rhombus":
                newShape = new fabric.Polygon([
                    {x: 50, y: 0}, {x: 100, y: 50}, {x: 50, y: 100}, {x: 0, y: 50}
                ], {left, top, fill: color, customType: "rhombus"});
                break;
            case "star":
                newShape = new fabric.Polygon([
                    {x: 50, y: 0}, {x: 61, y: 35}, {x: 98, y: 35}, {x: 68, y: 57},
                    {x: 79, y: 91}, {x: 50, y: 70}, {x: 21, y: 91}, {x: 32, y: 57},
                    {x: 2, y: 35}, {x: 39, y: 35}
                ], {left, top, fill: color, customType: "star"});
                break;
            case "hexagon":
                newShape = new fabric.Polygon([
                    {x: 50, y: 0}, {x: 100, y: 25}, {x: 100, y: 75},
                    {x: 50, y: 100}, {x: 0, y: 75}, {x: 0, y: 25}
                ], {left, top, fill: color, customType: "hexagon"});
                break;
            case "line":
                newShape = new fabric.Line([left, top, left + 100, top + 100], {
                    stroke: color,
                    strokeWidth: 5,
                    selectable: true,
                    hasControls: true
                });
                break;
            case "arrow":
                newShape = new fabric.Path("M 10 0 L 0 5 L 10 10 L 10 6 L 20 6 L 20 4 L 10 4 z", {
                    left,
                    top,
                    fill: color,
                    scaleX: 5,
                    scaleY: 5,
                    selectable: true,
                    hasControls: true,
                    customType: "arrow"
                });
                break;
            default:
                return;
        }

        makeObjectInteractive(newShape);
        canvas.add(newShape);
        canvas.setActiveObject(newShape);
        canvas.requestRenderAll();
    };


    useEffect(() => {
        const canvasMount = canvasMountRef.current;
        if (!canvasMount) return undefined;

        // Fabric encapsule le canvas dans un .canvas-container et déplace le
        // noeud <canvas>. Il ne doit donc jamais être un enfant géré par React :
        // un portail de menu (post-it, formes ou liens) ferait autrement échouer
        // le rapprochement React avec un NotFoundError / écran blanc.
        const canvasElement = mountFabricCanvas(canvasMount);
        if (!canvasElement) return undefined;
        canvasRef.current = canvasElement;

        const fabricCanvas = new fabric.Canvas(canvasElement, {
            isDrawingMode: false, // On désactive d'abord le mode dessin
            selection: true,
            uniformScaling: false,
            uniScaleKey: "shiftKey",
            preserveObjectStacking: true, //Si true, les objets ne remontent pas automatiquement en premier plan lorsqu'on les sélectionne.
            backgroundColor: "#FFFFFF",
        });

        // Désactive le mode dessin dès qu'un objet est sélectionné
        fabricCanvas.on("object:selected", () => {
            fabricCanvas.isDrawingMode = false;
            setDrawingMode(false); // Met à jour l'état pour le bouton
        });

        setCanvas(fabricCanvas);
        loadCanvasData(fabricCanvas);

        // Nettoyage lors du démontage du composant
        return () => {
            fabricCanvas.dispose();
            if (canvasRef.current === canvasElement) canvasRef.current = null;
            clearFabricCanvasHost(canvasMount);
        };
    }, [loadCanvasData]);

    useEffect(() => {
        if (!canvas || !TableauBlancStore.idCanvas) return;

        window.Pusher = Pusher;
        const echo = new Echo({
            broadcaster: 'pusher',
            key: process.env.REACT_APP_WS_KEY,
            secret: process.env.REACT_APP_WS_SECRET,
            wsHost: process.env.REACT_APP_WS_URL,
            wsPort: 6001,
            forceTLS: true,
            encrypted: true,
            disableStats: true,
            enabledTransports: ['ws', 'wss'],
            cluster: 'eu',
        });

        echo.channel(`${TableauBlancStore.idCanvas}-TableauBlanc_refreshChannel`)
            .listen('TableauBlanc\\CanvasUpdatedEvent', (event) => {
                const eventClientId = event?.clientId || event?.client_id;
                if (shouldSkipWhiteboardRefresh({
                    eventClientId,
                    currentClientId: TableauBlancStore.clientId,
                    ignoreUntil: ignoreRemoteCanvasRefreshUntilRef.current,
                })) return;
                loadCanvasData(canvas);
            });

        return () => echo.disconnect();
    }, [canvas, TableauBlancStore.idCanvas, loadCanvasData]);

    const recenterMap = () => {
        if (canvas) {
            canvas.setViewportTransform([1, 0, 0, 1, 0, 0]); // Reset zoom et position
            canvas.setZoom(1); // Zoom à 100%
            canvas.requestRenderAll();
            const vpt = canvas.viewportTransform;
            setCanvasSettings(prev => ({
                ...prev,
                miniMap: {
                    left: -vpt[4],
                    top: -vpt[5],
                    zoom: canvas.getZoom()
                }
            }));

            setViewportInfo({
                zoom: 1,
                x: 0,
                y: 0,
            });
        }
    };

    useEffect(() => {
        if (canvas) {
            const hasChanged = canvas.backgroundColor !== canvasSettings.backgroundColor;
            canvas.setBackgroundColor(canvasSettings.backgroundColor, canvas.renderAll.bind(canvas));
            if (hasChanged && !isLoadingCanvasRef.current) {
                recordCanvasHistory();
                scheduleCanvasSave();
            }
        }
    }, [canvas, canvasSettings.backgroundColor, recordCanvasHistory, scheduleCanvasSave]);

    useEffect(() => {
        if (!canvasRef.current || !canvas || !canvas.lowerCanvasEl) return;

        const parent = canvasRef.current.closest(".canvasTableauBlanc");
        if (!parent) return;

        const updateCanvasSize = () => {
            const width = Math.floor(parent.clientWidth);
            const height = Math.floor(parent.clientHeight);
            if (!width || !height) return;

            canvas.setDimensions({width, height});
            canvas.calcOffset();
            canvas.requestRenderAll();
        };

        // La hauteur utile change aussi lorsque le footer ou la barre du navigateur
        // tactile change de taille. ResizeObserver couvre
        // ces cas sans dépendre d'un rafraîchissement manuel.
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateCanvasSize) : null;
        observer?.observe(parent);
        window.addEventListener("resize", updateCanvasSize);
        window.visualViewport?.addEventListener("resize", updateCanvasSize);
        updateCanvasSize();

        return () => {
            observer?.disconnect();
            window.removeEventListener("resize", updateCanvasSize);
            window.visualViewport?.removeEventListener("resize", updateCanvasSize);
        };
    }, [canvas]);

    const passerModeSelection = applySelectionMode;

    const passerModeDessin = () => {
        if (canvas) {
            canvas.isDrawingMode = true;
            canvas.skipTargetFind = false;
            canvas.defaultCursor = "crosshair";
            canvas.hoverCursor = "crosshair";
            canvas.freeDrawingBrush.color = drawingSettings.color;
            canvas.freeDrawingBrush.width = drawingSettings.brushSize; // Épaisseur plus grande pour effacer
            setDrawingMode(true); // Met à jour l'état pour le bouton
            setIsErasing(false); // Réinitialise le mode gomme
            setPanMode(false);
            cancelConnectorMode();
        }
    }

    const passerModeDeplacement = () => {
        if (!canvas) return;
        canvas.discardActiveObject();
        canvas.isDrawingMode = false;
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.defaultCursor = "grab";
        canvas.hoverCursor = "grab";
        canvas.requestRenderAll();
        setDrawingMode(false);
        setIsErasing(false);
        setPanMode(true);
        cancelConnectorMode();
        setSidebarVisible(false);
    };

    const toggleErasingMode = () => {
        if (canvas) {
            const nextErasing = !isErasing;
            if (!nextErasing) {
                passerModeSelection();
                return;
            }

            setIsErasing(nextErasing);
            canvas.discardActiveObject();
            canvas.isDrawingMode = false;
            canvas.selection = false;
            canvas.skipTargetFind = false;
            canvas.defaultCursor = "not-allowed";
            canvas.hoverCursor = "not-allowed";
            canvas.requestRenderAll();
            setDrawingMode(false);
            setPanMode(false);
            setSelectedObject(null);
            cancelConnectorMode();
        }
    };

    const deleteSelectedObject = () => {
        if (canvas) {
            const activeObjects = canvas.getActiveObjects(); // Récupère tous les objets sélectionnés
            if (activeObjects.length > 0) {
                canvas.discardActiveObject();
                activeObjects.forEach((object) => {
                    canvas.remove(object); // Supprime chaque objet sélectionné
                });
                canvas.renderAll(); // Rendu du canevas après modification
                setSelectedObject(null);
            }
        }
    };

    const verrouillerSelectedObject = () => {
        if (canvas) {
            const activeObjects = canvas.getActiveObjects();
            if (activeObjects.length > 0) {
                activeObjects.forEach((object) => {
                    object.lockMovementX = true;
                    object.lockMovementY = true;
                    object.lockScalingX = true;
                    object.lockScalingY = true;
                    object.lockRotation = true;
                });
                canvas.renderAll();
                recordCanvasHistory();
                scheduleCanvasSave();
            }
        }
    };
    const deverrouillerSelectedObject = () => {
        if (canvas) {
            const activeObjects = canvas.getActiveObjects();
            if (activeObjects.length > 0) {
                activeObjects.forEach((object) => {
                    object.lockMovementX = false;
                    object.lockMovementY = false;
                    object.lockScalingX = false;
                    object.lockScalingY = false;
                    object.lockRotation = false;
                });
                canvas.renderAll();
                recordCanvasHistory();
                scheduleCanvasSave();
            }
        }
    };


    const addText = () => {
        if (!canvas) return;
        const {left, top} = getInsertPosition();

        const text = new fabric.Textbox("Votre texte ici", {
            left,
            top,
            width: 240,
            fontSize: textSettings.fontSize,
            fill: textSettings.color, // Utilise la couleur sélectionnée
            fontFamily: textSettings.fontFamily,
            fontWeight: textSettings.fontWeight,
            fontStyle: textSettings.fontStyle,
            selectable: true,
            hasControls: true,
            editable: true,
            customType: "text",
            objectId: nextObjectId("text"),
        });

        makeObjectInteractive(text);
        canvas.add(text);
        canvas.setActiveObject(text);
        canvas.renderAll();
        window.setTimeout(() => {
            text.enterEditing();
            text.selectAll();
        }, 0);
    };

    const addStickyNote = (fill = "#FFE68A", requestedFormat = stickyFormat) => {
        if (!canvas) return;

        const format = STICKY_FORMATS.find(option => option.id === requestedFormat) || STICKY_FORMATS[1];
        const {left, top} = getInsertPosition(16);
        const note = new fabric.Textbox("Double-cliquez pour écrire", {
            left,
            top,
            width: format.width,
            minWidth: 180,
            fontFamily: "Sofia Pro",
            fontSize: 19,
            fontWeight: "600",
            fill: "#173843",
            lineHeight: 1.28,
            backgroundColor: fill,
            padding: 22,
            textAlign: "left",
            stroke: "rgba(23,56,67,.10)",
            strokeWidth: 1,
            shadow: new fabric.Shadow({color: "rgba(0,35,53,.18)", blur: 16, offsetX: 0, offsetY: 8}),
            editable: true,
            splitByGrapheme: false,
            customType: "sticky_note",
            objectId: nextObjectId("sticky"),
            isPlaceholder: true,
            stickyFormat: format.id,
            stickyMinHeight: format.minHeight,
            stickyPadding: format.padding,
        });

        makeObjectInteractive(note);
        canvas.add(note);
        canvas.setActiveObject(note);
        canvas.requestRenderAll();
        closeToolMenu();
    };

    const startConnectorMode = (style) => {
        if (!canvas) return;

        // Ce clic ne doit pas remonter dans le cycle de réinitialisation
        // global du canvas : la palette est dans un portal et le cycle
        // précédent recréait les listeners Fabric au moment de l'activation.
        setToolMenu(null);
        connectorStartRef.current = null;
        connectorModeRef.current = style;
        setConnectorHint("Sélectionnez le premier objet à relier");
        canvas.isDrawingMode = false;
        canvas.selection = true;
        canvas.skipTargetFind = false;
        canvas.defaultCursor = "default";
        canvas.hoverCursor = "move";
        setDrawingMode(false);
        setIsErasing(false);
        setPanMode(false);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
    };

    const cancelConnectorMode = () => {
        connectorStartRef.current = null;
        connectorModeRef.current = null;
        setConnectorHint(null);
    };

    const changeZoom = (delta) => {
        if (!canvas) return;
        const nextZoom = Math.min(2.2, Math.max(.45, canvas.getZoom() + delta));
        canvas.zoomToPoint(new fabric.Point(canvas.getWidth() / 2, canvas.getHeight() / 2), nextZoom);
        canvas.requestRenderAll();
        const vpt = canvas.viewportTransform;
        setViewportInfo({zoom: nextZoom, x: -vpt[4] / nextZoom, y: -vpt[5] / nextZoom});
    };

    const toggleFullscreen = () => {
        const root = whiteboardRootRef.current;
        if (!root) return;
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            root.requestFullscreen?.();
        }
    };

    const createPlanningAction = async (payload) => {
        setIsCreatingAction(true);
        try {
            const response = await TableauBlancStore.createPlanningAction(payload);
            setCreatedPlanningTask(response?.data?.task || null);
        } finally {
            setIsCreatingAction(false);
        }
    };

    const openPlanningAction = (task) => {
        const planningWidget = widgetStore.widgetsAvailable.find(widget =>
            widget.typeWidget === "WidgetPlanningActions" && String(widget.configWidget) === String(task.planning_id)
        );
        if (!planningWidget) return;

        uiStore.openConsultingModePlan2do(
            PlanningActionsStore,
            widgetStore.getCookie("current_apiToken"),
            planningWidget.idWidget,
            task.planning_id,
            "Kanban",
        );
        window.setTimeout(() => PlanningActionsStore.openModalFormAction(null, null, null, task.id), 400);
        setActionModalOpen(false);
    };

    // Fonctions pour ajouter une image au canevas
    const addImage = () => {
        fileInputRef.current.click();
    }
    const addImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                fabric.Image.fromURL(e.target.result, (img) => {
                    img.scaleToWidth(200); // Redimensionner l'image
                    const {left, top} = getInsertPosition();
                    img.set({
                        left,
                        top,
                        selectable: true, // L'image peut être déplacée
                        stroke: null, //pas de bordure
                        strokeWidth: 0, // pas d'épaisseur
                        strokeUniform: true // Garde uniforme si ajout d'une bordure
                    });
                    makeObjectInteractive(img);
                    canvas.add(img);
                    canvas.renderAll();
                });
            };
            reader.readAsDataURL(file);
        }
    };

    useEffect(() => {
        if (bibliothequeImagesStore.selectedImageModule) {
            const imageUrl = process.env.REACT_APP_API_URL + "/bibliotheque/image/" + bibliothequeImagesStore.selectedImageModule;

            fabric.Image.fromURL(imageUrl, (img) => {
                img.scaleToWidth(200);
                const {left, top} = getInsertPosition(24);
                img.set({
                    left,
                    top,
                    selectable: true,
                    stroke: null,
                    strokeWidth: 0,
                    strokeUniform: true
                });
                makeObjectInteractive(img);
                canvas.add(img);
                canvas.renderAll();
            }, {crossOrigin: 'anonymous'});

            bibliothequeImagesStore.onResetImageModule();
        }
    }, [bibliothequeImagesStore, bibliothequeImagesStore.selectedImageModule, getInsertPosition, makeObjectInteractive]);


    useEffect(() => {
        if (!canvas || !selectedObject) return;

        if (selectedObject.type === "textbox") {
            selectedObject.set({
                fontSize: textSettings.fontSize,
                fontFamily: textSettings.fontFamily,
                fontWeight: textSettings.fontWeight,
                fontStyle: textSettings.fontStyle,
                fill: textSettings.color,
                backgroundColor: textSettings.backgroundColor || '', // Important: Fabric.js n'aime pas "null" directement
            });
        } else if (selectedObject.type === "image") {
            selectedObject.set({
                stroke: imageSettings.stroke,
                strokeWidth: imageSettings.strokeWidth,
            });
        } else if (["rect", "circle", "triangle", "polygon"].includes(selectedObject.type)) {
            selectedObject.set({
                fill: shapeSettings.fill,
                backgroundColor: shapeSettings.backgroundColor || '',
                customType: shapeSettings.customType
            });
        } else if (selectedObject.type === "line") {
            selectedObject.set({
                stroke: shapeSettings.fill,
                strokeWidth: 5,
            });
        } else {
            if (selectedObject.customType === "arrow") {
                selectedObject.set({
                    fill: shapeSettings.fill,
                    backgroundColor: shapeSettings.backgroundColor || '',
                    customType: "arrow"
                });
            } else {
                console.log("Type d'objet non pris en charge pour la mise à jour", selectedObject.type);
            }

        }

        canvas.requestRenderAll();

        recordCanvasHistory();
        scheduleCanvasSave();
    }, [textSettings, shapeSettings, imageSettings, canvas, recordCanvasHistory, scheduleCanvasSave]);


    useEffect(() => {
        if (canvas) {
            switch (drawingSettings.brushType) {
                case "PencilBrush":
                    canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
                    break;
                case "SprayBrush":
                    canvas.freeDrawingBrush = new fabric.SprayBrush(canvas);
                    canvas.freeDrawingBrush.density = 10; // Réglage de l'aérographe
                    break;
                case "CircleBrush":
                    canvas.freeDrawingBrush = new fabric.CircleBrush(canvas);
                    break;
                default:
                    canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
            }
            canvas.freeDrawingBrush.color = drawingSettings.color;
            canvas.freeDrawingBrush.width = drawingSettings.brushSize;
        }
    }, [drawingSettings, canvas]);


    return (
        <div ref={whiteboardRootRef} className={"contenantEntier"}>
            <SidebarWidgets widget={"TableauBlanc"}/>
            <header className="wbTopbar">
                <div className="wbTitle">
                    <span className="wbTitleIcon">◫</span>
                    <div className="wbTitleMeta"><span className="wbEyebrow">TABLEAU BLANC</span><strong className="wbTitleName">{tbNom || "Espace de travail"}</strong><span className="wbStatus"><i className="wbStatusDot"/>Synchronisation active{lastSaveTime ? ` · enregistré à ${lastSaveTime.toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"})}` : ""}</span></div>
                </div>
                <div className="wbTopbarActions">
                    <button type="button" className="wbTopbarAction" onClick={() => { TableauBlancStore.idCanvas = null; }}>Tableaux</button>
                    <button type="button" className="wbTopbarAction wbFullscreenToggle" onClick={toggleFullscreen}>{isFullscreen ? "Quitter" : "Plein écran"}</button>
                    <button type="button" className="wbTopbarAction primary" onClick={() => { setCreatedPlanningTask(null); setActionModalOpen(true); }}>Créer une action</button>
                </div>
            </header>
            <input type="file" ref={fileInputRef} style={{display: "none"}} accept="image/*" onChange={addImageChange}/>
            <div className={"canvasTableauBlanc wbCanvasSurface"}>
                <div className="wbToolDock" role="toolbar" aria-label="Outils Tableau Blanc">
                    <div className="wbToolGroup">
                        <button type="button" className={`wbToolButton labelled ${!drawingMode && !panMode ? "isActive" : ""}`} onClick={passerModeSelection} title="Sélectionner et déplacer">↖<span>Sélection</span></button>
                        <button type="button" className={`wbToolButton labelled ${panMode ? "isActive" : ""}`} onClick={passerModeDeplacement} title="Déplacer le tableau">✥<span>Déplacer</span></button>
                        <button type="button" className={`wbToolButton labelled ${drawingMode && !isErasing ? "isActive" : ""}`} onClick={() => { passerModeDessin(); setSidebarVisible(true); }} title="Dessiner">✎<span>Dessiner</span></button>
                        <button type="button" className={`wbToolButton labelled ${isErasing ? "isActive" : ""}`} onClick={toggleErasingMode} title="Gomme : cliquer un objet pour le supprimer">⌫<span>Gomme</span></button>
                    </div>
                    <div className="wbToolGroup">
                        <button type="button" data-wb-tool-trigger="sticky" className={`wbToolButton labelled ${toolMenu === "sticky" ? "isActive" : ""}`} onClick={(event) => toggleToolMenu("sticky", event)} title="Ajouter un post-it" aria-expanded={toolMenu === "sticky"}>▣<span>Post-it</span></button>
                        <button type="button" className="wbToolButton labelled" onClick={addText} title="Ajouter un texte">T<span>Texte</span></button>
                        <button type="button" data-wb-tool-trigger="shape" className={`wbToolButton labelled ${toolMenu === "shape" ? "isActive" : ""}`} onClick={(event) => toggleToolMenu("shape", event)} title="Ajouter une forme" aria-expanded={toolMenu === "shape"}>◇<span>Forme</span></button>
                        <button type="button" data-wb-tool-trigger="connector" className={`wbToolButton labelled ${connectorHint || toolMenu === "connector" ? "isActive" : ""}`} onClick={(event) => toggleToolMenu("connector", event)} title="Relier deux objets" aria-expanded={toolMenu === "connector"}>↔<span>Lier</span></button>
                        <button type="button" className="wbToolButton labelled" onClick={addImage} title="Ajouter une image">▧<span>Image</span></button>
                    </div>
                    <div className="wbToolGroup">
                        <button type="button" className="wbToolButton" onClick={undoCanvas} disabled={!historyState.canUndo} title="Annuler">↶</button>
                        <button type="button" className="wbToolButton" onClick={redoCanvas} disabled={!historyState.canRedo} title="Rétablir">↷</button>
                        <button type="button" className="wbToolButton" onClick={() => changeZoom(-.1)} title="Réduire">−</button>
                        <button type="button" className="wbToolButton" onClick={() => changeZoom(.1)} title="Agrandir">＋</button>
                        <button type="button" className="wbToolButton" onClick={recenterMap} title="Recentrer">⌖</button>
                    </div>
                </div>
                {toolMenu === "sticky" && createPortal(
                    <div className="wbFloatingToolMenu wbStickyMenuPanel" style={toolMenuPosition} role="menu" aria-label="Choisir le format et la couleur du post-it">
                        <span>Format du post-it</span>
                        <div className="wbStickyFormatGrid">
                            {STICKY_FORMATS.map(format => (
                                <button key={format.id} type="button" className={stickyFormat === format.id ? "isSelected" : ""} onClick={() => setStickyFormat(format.id)} title={`Format ${format.label}`} aria-label={`Choisir le format ${format.label}`}>
                                    <b>{format.icon}</b><small>{format.label}</small>
                                </button>
                            ))}
                        </div>
                        <span>Couleur du post-it</span>
                        <div className="wbStickyColorGrid">
                            {STICKY_COLORS.map(({label, value}) => (
                                <button key={value} type="button" title={label} aria-label={`Créer un post-it ${label.toLowerCase()}`} style={{backgroundColor: value}} onClick={() => addStickyNote(value, stickyFormat)}/>
                            ))}
                        </div>
                    </div>,
                    document.body,
                )}
                {toolMenu === "shape" && createPortal(
                    <div className="wbFloatingToolMenu wbConnectorMenuPanel" style={toolMenuPosition} role="menu" aria-label="Choisir une forme">
                        <span>Ajouter une forme</span>
                        <button type="button" onClick={() => addShape("rect")}><b>□</b>Rectangle</button>
                        <button type="button" onClick={() => addShape("circle")}><b>○</b>Cercle</button>
                        <button type="button" onClick={() => addShape("triangle")}><b>△</b>Triangle</button>
                        <button type="button" onClick={() => addShape("line")}><b>—</b>Ligne</button>
                        <button type="button" onClick={() => addShape("arrow")}><b>→</b>Flèche</button>
                    </div>,
                    document.body,
                )}
                {toolMenu === "connector" && createPortal(
                    <div className="wbFloatingToolMenu wbConnectorMenuPanel" style={toolMenuPosition} role="menu" aria-label="Créer une liaison entre deux objets">
                        <span>Relier deux objets</span>
                        {CONNECTOR_STYLES.map(style => (
                            <button key={style.id} type="button" onClick={() => startConnectorMode(style.id)}><b>{style.icon}</b>{style.label}</button>
                        ))}
                    </div>,
                    document.body,
                )}
                {connectorHint && (
                    <div className="wbConnectorHint" role="status">
                        <span>↔</span><strong>{connectorHint}</strong><button type="button" onClick={cancelConnectorMode}>Annuler</button>
                    </div>
                )}
                <div ref={canvasMountRef} className="wbFabricCanvasMount" aria-label="Canevas Tableau Blanc"/>
                <MiniMap recenterMap={recenterMap} viewportInfo={viewportInfo}/>
                <div className="wbCanvasHint"><strong>{Math.round(viewportInfo.zoom * 100)}%</strong><span>Activez Déplacer ou Alt + glisser pour naviguer</span></div>

                <CanvaSidebar
                    sidebarVisible={sidebarVisible}
                    selectedObject={selectedObject}
                    setSidebarVisible={setSidebarVisible}
                    resetToSelection={applySelectionMode}
                    sendToBack={sendToBack}
                    bringToFront={bringToFront}
                    moveBackward={moveBackward}
                    moveForward={moveForward}
                    drawingMode={drawingMode}
                    deleteSelectedObject={deleteSelectedObject}

                    drawingSettings={drawingSettings} setDrawingSettings={setDrawingSettings}
                    textSettings={textSettings} setTextSettings={setTextSettings}
                    imageSettings={imageSettings} setImageSettings={setImageSettings}
                    shapeSettings={shapeSettings} setShapeSettings={setShapeSettings}
                    changeSelectedShapeType={changeSelectedShapeType}

                    copyObject={copyObject}
                    pasteObject={pasteObject}
                />

            </div>


            {/* Menu contextuel */}
            {selectedObject && (
                <ContextMenu
                    menuPosition={menuPosition}
                    selectedObject={selectedObject}
                    deleteSelectedObject={deleteSelectedObject}
                    verrouillerSelectedObject={verrouillerSelectedObject}
                    deverrouillerSelectedObject={deverrouillerSelectedObject}
                    copyObject={copyObject}
                    createPlanningAction={() => { setCreatedPlanningTask(null); setActionModalOpen(true); }}
                />
            )}
            {actionModalOpen && (
                <WhiteboardActionModal
                    canvasName={tbNom || "Tableau Blanc"}
                    selectedObject={selectedObject}
                    isSubmitting={isCreatingAction}
                    createdTask={createdPlanningTask}
                    onClose={() => { setActionModalOpen(false); setCreatedPlanningTask(null); }}
                    onCreate={createPlanningAction}
                    onOpenPlanning={openPlanningAction}
                />
            )}
        </div>


    );
});

export default Canva;
